import { useState, useEffect, useRef, useCallback } from "react";
import { X, Settings, Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { clearAttributionOnConsentWithdrawal, captureAttribution } from "@/lib/attribution";
import { getStoredConsent, saveConsent } from "@/lib/consent";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    openConsentModal?: () => void;
    __loadMetaPixelIfConsented?: () => void;
  }
}

/** Meta has no consent-mode equivalent, so the Pixel only ever loads once
 * advertising consent is actually granted (see the loader in index.html) -
 * call this the instant that happens so a visitor who accepts right away
 * doesn't wait for the idle/interaction fallback in index.html to load it. */
function loadMetaPixelIfConsented() {
  if (typeof window.__loadMetaPixelIfConsented === "function") {
    window.__loadMetaPixelIfConsented();
  }
}

function updateGtagConsent(analytics: boolean, ads: boolean) {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: ads ? "granted" : "denied",
      analytics_storage: analytics ? "granted" : "denied",
      ad_user_data: ads ? "granted" : "denied",
      ad_personalization: ads ? "granted" : "denied",
    });
  }
}

export default function ConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [adsEnabled, setAdsEnabled] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const closeModal = useCallback(() => {
    setShowModal(false);
    if (!getStoredConsent()) setShowBanner(true);
  }, []);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      // No consent on file, or it just expired (getStoredConsent applies
      // the 180-day expiry and clears the record). The inline bootstrap
      // script in index.html applies the same expiry before this runs, so
      // it won't have granted consent from a stale record either - but
      // explicitly re-asserting "denied" here means Google Ads/GTM consent
      // is revoked even if that bootstrap check is ever bypassed (e.g. a
      // future change removes it), rather than relying on it silently.
      updateGtagConsent(false, false);
      setShowBanner(true);
    } else {
      setAnalyticsEnabled(stored.analytics);
      setAdsEnabled(stored.ads);
    }
    
    window.openConsentModal = () => {
      const stored = getStoredConsent();
      if (stored) {
        setAnalyticsEnabled(stored.analytics);
        setAdsEnabled(stored.ads);
      }
      setShowBanner(false);
      setShowModal(true);
    };
    
    return () => {
      delete window.openConsentModal;
    };
  }, []);

  // ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showModal) {
        closeModal();
      }
    };
    
    if (showModal) {
      document.addEventListener("keydown", handleKeyDown);
      // Focus the close button when modal opens
      setTimeout(() => closeButtonRef.current?.focus(), 0);
    }
    
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showModal, closeModal]);

  const handleAcceptAll = () => {
    saveConsent({ analytics: true, ads: true });
    updateGtagConsent(true, true);
    loadMetaPixelIfConsented();
    // Advertising consent just turned on: capture the *current* landing
    // URL/referrer right away. Without this, a visitor who lands on an ad
    // URL and immediately accepts the banner (before navigating anywhere
    // else) would have their landing touch missed entirely, since capture
    // otherwise only runs on route changes.
    captureAttribution();
    setAnalyticsEnabled(true);
    setAdsEnabled(true);
    setShowBanner(false);
    setShowModal(false);
  };

  const handleRejectNonEssential = () => {
    saveConsent({ analytics: false, ads: false });
    updateGtagConsent(false, false);
    clearAttributionOnConsentWithdrawal();
    setAnalyticsEnabled(false);
    setAdsEnabled(false);
    setShowBanner(false);
    setShowModal(false);
  };

  const handleSavePreferences = () => {
    saveConsent({ analytics: analyticsEnabled, ads: adsEnabled });
    updateGtagConsent(analyticsEnabled, adsEnabled);
    if (adsEnabled) {
      // Same reasoning as handleAcceptAll: capture the current landing
      // touch as soon as advertising consent turns on, not just on the
      // next route change.
      captureAttribution();
      loadMetaPixelIfConsented();
    } else {
      // Withdrawing advertising consent must wipe any marketing
      // attribution already captured under the old (allowed) consent
      // state immediately - not just block future capture - so it can
      // never ride along on a booking submitted right after this change.
      clearAttributionOnConsentWithdrawal();
    }
    setShowBanner(false);
    setShowModal(false);
  };

  const openModal = () => {
    setShowModal(true);
  };

  
  return (
    <>
      {showBanner && !showModal && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-[9999] bg-[#1a1a1a] border-t border-[#333] p-4 md:p-6 shadow-2xl"
          data-testid="consent-banner"
          role="dialog"
          aria-label="Cookie consent"
          aria-describedby="consent-description"
        >
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Cookie className="w-5 h-5 text-[#cbb26b]" />
                  <h3 className="font-semibold text-white text-lg">Cookie Preferences</h3>
                </div>
                <p id="consent-description" className="text-[#999] text-sm leading-relaxed">
                  We use cookies to enhance your experience, analyse site traffic, and for marketing purposes. 
                  You can choose which cookies you allow.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRejectNonEssential}
                  className="border-[#444] text-white hover:bg-[#333]"
                  data-testid="button-reject-cookies"
                >
                  Reject All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openModal}
                  className="border-[#444] text-white hover:bg-[#333]"
                  data-testid="button-manage-cookies"
                >
                  <Settings className="w-4 h-4 mr-1" />
                  Manage
                </Button>
                <Button
                  size="sm"
                  onClick={handleAcceptAll}
                  className="bg-[#cbb26b] hover:bg-[#b39f5c] text-[#0d0d0d]"
                  data-testid="button-accept-cookies"
                >
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div 
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/70"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
          role="presentation"
        >
          <div 
            ref={modalRef}
            className="bg-[#1a1a1a] border border-[#333] rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            data-testid="consent-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-modal-title"
            aria-describedby="cookie-modal-description"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 id="cookie-modal-title" className="text-xl font-bold text-white">Cookie Settings</h2>
                <button
                  ref={closeButtonRef}
                  onClick={closeModal}
                  className="text-[#666] hover:text-white transition-colors"
                  data-testid="button-close-modal"
                  aria-label="Close cookie settings"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p id="cookie-modal-description" className="text-[#999] text-sm mb-6 leading-relaxed">
                We use different types of cookies to operate and improve our website. 
                You can adjust your preferences below.
              </p>

              <div className="space-y-4 mb-8">
                <div className="p-4 bg-[#111] rounded-lg border border-[#333]">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-white">Essential Cookies</h3>
                    <span className="text-xs text-[#cbb26b] bg-[#cbb26b]/10 px-2 py-1 rounded">Always On</span>
                  </div>
                  <p className="text-[#999] text-sm">
                    Required for the website to function. Cannot be disabled.
                  </p>
                </div>

                <div className="p-4 bg-[#111] rounded-lg border border-[#333]">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-white">Analytics Cookies</h3>
                    <Switch
                      checked={analyticsEnabled}
                      onCheckedChange={setAnalyticsEnabled}
                      data-testid="switch-analytics"
                      aria-label="Enable analytics cookies"
                    />
                  </div>
                  <p className="text-[#999] text-sm">
                    Help us understand how visitors interact with our website by collecting anonymous information.
                  </p>
                </div>

                <div className="p-4 bg-[#111] rounded-lg border border-[#333]">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-white">Advertising Cookies</h3>
                    <Switch
                      checked={adsEnabled}
                      onCheckedChange={setAdsEnabled}
                      data-testid="switch-advertising"
                      aria-label="Enable advertising cookies"
                    />
                  </div>
                  <p className="text-[#999] text-sm">
                    Used to show you relevant adverts and measure the effectiveness of our advertising campaigns.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-[#444] text-white hover:bg-[#333]"
                  onClick={handleRejectNonEssential}
                  data-testid="button-modal-reject"
                >
                  Reject All
                </Button>
                <Button
                  className="flex-1 bg-[#cbb26b] hover:bg-[#b39f5c] text-[#0d0d0d]"
                  onClick={handleSavePreferences}
                  data-testid="button-save-preferences"
                >
                  Save Preferences
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function CookieSettingsLink() {
  const handleClick = () => {
    if (window.openConsentModal) {
      window.openConsentModal();
    }
  };

  return (
    <button
      onClick={handleClick}
      className="text-muted-foreground hover:text-foreground transition-colors text-sm underline underline-offset-4"
      data-testid="link-cookie-settings"
    >
      Cookie Settings
    </button>
  );
}
